import { HotkeyScope, useContextHotkeys } from "@/hooks/useContextTree";
import { useCallback, useEffect, useMemo, useState } from "react";

import { CommandRegistrations } from "@/app/app/_/commands";
import { CreateNodeCommand } from "@/app/app/_/commands/CreateNodeCommand";
import { SearchCommand } from "@/app/app/_/commands/SearchCommand";
import { ToggleColorSchemeCommand } from "@/app/app/_/commands/ToggleColorSchemeCommand";
import { TogglePanelVisibilityCommand } from "@/app/app/_/commands/TogglePanelVisibilityCommand";
import { NavigateAppViewCommands } from "@/app/app/_/commands/NavigateAppViewCommands";
import { ToggleSidebarVisibilityCommand } from "@/app/app/_/commands/ToggleSidebarVisibilityCommand";
import { ToggleStyleThemeCommand } from "@/app/app/_/commands/ToggleStyleThemeCommand";
import { ManageCategoriesCommand } from "@/app/app/_/commands/ManageCategoriesCommand";
import { ManageTagsCommand } from "@/app/app/_/commands/ManageTagsCommand";
import { UpdateNodeCommand } from "@/app/app/_/commands/UpdateNodeCommand";
import { Buttons } from "@/components/atoms/Buttons";
import { Dialog } from "@/components/organisms/Dialog";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { ObjectType } from "@/objects/@types";
import { getCreatableObjects, getUpdatableObjects } from "@/objects";
import { State } from "@/state";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Typography } from "@/components/ui/typography";
import { CommandCenterTopbar } from "./CommandCenterTopbar";

export function CommandCenter() {
  const commandIdOfQuickActions = State(
    (store) => store.commandDefinitionIdOfQuickActions,
  );
  const commandInstance = State(
    (state) =>
      state.commandInstanceStack[state.commandInstanceStack.length - 1],
  );
  const commandInstanceStack = State((state) => state.commandInstanceStack);
  const activeCommand = State((state) =>
    state.commandDefinitions.find(
      (command) =>
        command.id ===
        state.commandInstanceStack[state.commandInstanceStack.length - 1]
          ?.commandDefinitionId,
    ),
  );
  const clearCommandStack = State((state) => state.clearCommandStack);
  const popCommandsFromStack = State((state) => state.popCommandsFromStack);
  const pushToCommandStack = State((state) => state.pushToCommandStack);
  const dialogSize = State((state) => state.dialogSize);
  const dialogIsFullScreen = State((state) => state.dialogIsFullScreen);
  const isOpen = activeCommand != null;
  const onClose = useCallback(() => {
    if (
      activeCommand?.getIsConfirmationRequiredOnExit &&
      activeCommand.getIsConfirmationRequiredOnExit(
        commandInstance.commandInstanceId,
      )
    ) {
      setConfirmExit(true);
    } else {
      popCommandsFromStack();
      State.setState({
        dialogSize: null,
        dialogLayoutMode: null,
        dialogIsFullScreen: false,
      });
    }
  }, [activeCommand]);

  const [confirmExit, setConfirmExit] = useState(false);

  // Setup a global hotkey to open the command center, command + k
  // if on mac, use command, if on windows, use control
  const handleCommandK = useCallback(
    (event: KeyboardEvent) => {
      State.getState().setCommandSearchValue("");
      clearCommandStack();
      pushToCommandStack({ commandDefinitionId: commandIdOfQuickActions });
    },
    [commandIdOfQuickActions, clearCommandStack, pushToCommandStack],
  );

  useContextHotkeys({
    combos: { mac: "meta+k", windows: "ctrl+k", linux: "ctrl+k" },
    callback: handleCommandK,
    id: "command-center-open",
    // App-level palette — works inside Match panel (``resetHotkeys`` without
    // ``suppressGlobalHotkeys``). Overlays still block it via suppressGlobal.
    scopeCheck: HotkeyScope.global,
  });

  const header = useMemo(() => {
    if (!activeCommand?.header || !commandInstance) {
      return null;
    }
    return (
      <activeCommand.header
        commandInstanceId={commandInstance.commandInstanceId}
        commandDefinitionId={commandInstance.commandDefinitionId}
        context={commandInstance.context}
      />
    );
  }, [activeCommand, commandInstance]);

  const footer = useMemo(() => {
    if (
      !activeCommand?.footer ||
      !commandInstance ||
      activeCommand.hasFooter?.(commandInstance.context) === false
    ) {
      return null;
    }
    return (
      <activeCommand.footer
        commandInstanceId={commandInstance.commandInstanceId}
        commandDefinitionId={commandInstance.commandDefinitionId}
        context={commandInstance.context}
      />
    );
  }, [activeCommand, commandInstance]);

  const children = useMemo(() => {
    if (!activeCommand?.component || !commandInstance) {
      return null;
    }
    return (
      <activeCommand.component
        commandInstanceId={commandInstance.commandInstanceId}
        commandDefinitionId={commandInstance.commandDefinitionId}
        context={commandInstance.context}
      />
    );
  }, [activeCommand, commandInstance]);

  // When the active command changes, reset dialog toggles
  useEffect(() => {
    if (!activeCommand) return;
    State.setState({
      dialogSize: null,
      dialogLayoutMode: null,
      dialogIsFullScreen: false,
    });
  }, [activeCommand]);

  useEffect(() => {
    return () => {
      if (
        activeCommand?.onExit &&
        !commandInstanceStack
          .map((commandInstance) => commandInstance.commandInstanceId)
          .includes(commandInstance.commandInstanceId)
      ) {
        activeCommand.onExit(commandInstance.commandInstanceId);
      }
    };
  }, [activeCommand, commandInstance, commandInstanceStack]);

  const commandPaletteContentClassName = dialogIsFullScreen
    ? undefined
    : activeCommand?.commandPaletteContentClassName;

  const isDialogFullScreen =
    dialogIsFullScreen || (activeCommand?.isFullScreen ?? false);
  const isDialogConstrained =
    !dialogIsFullScreen && (activeCommand?.isConstrained ?? false);
  const commandLayoutFillsHeight = isDialogFullScreen || isDialogConstrained;

  return (
    <>
      <SearchCommand />
      <ToggleColorSchemeCommand />
      <ToggleStyleThemeCommand />
      <ToggleSidebarVisibilityCommand />
      <NavigateAppViewCommands />
      <TogglePanelVisibilityCommand />
      <CommandRegistrations />

      {Object.values(getCreatableObjects()).map((object: ObjectType<any>) => {
        return (
          <CreateNodeCommand key={object.typename} typename={object.typename} />
        );
      })}

      {Object.values(getUpdatableObjects()).map((object: ObjectType<any>) => {
        return (
          <UpdateNodeCommand key={object.typename} typename={object.typename} />
        );
      })}

      <ManageCategoriesCommand />
      <ManageTagsCommand />

      {/* Search dialog (default) */}
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        isConstrained={(() => {
          // If fullscreen is toggled on, not constrained
          if (dialogIsFullScreen) return false;
          // Otherwise use command's default
          return activeCommand?.isConstrained ?? false;
        })()}
        isFullScreen={(() => {
          // If fullscreen toggle is on, always fullscreen
          if (dialogIsFullScreen) return true;
          // Otherwise use command's default
          return activeCommand?.isFullScreen ?? false;
        })()}
        renderKey={activeCommand?.id}
      >
        <StickyLayout
          fillHeight={commandLayoutFillsHeight}
          stickyTopChildren={
            <div
              // pl: 0.5,
              // pr: 0.5,
              className="flex flex-col gap-1 pt-1 pb-1 w-full h-min"
            >
              <CommandCenterTopbar />
              <div className="flex items-center justify-between w-full min-h-min">
                {header}
              </div>
            </div>
          }
          stickyTopClassName="h-min"
          stickyBottomChildren={footer}
          // The dialog's own ground: what scrolls behind it does not show through.
          stickyBottomClassName="bg-popover"
          childrenClassName={commandPaletteContentClassName}
        >
          {children}
        </StickyLayout>
      </Dialog>

      <AlertDialog
        open={confirmExit}
        onOpenChange={(open) => !open && setConfirmExit(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Exit?</AlertDialogTitle>
            <AlertDialogDescription>
              You will lose any unsaved changes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                clearCommandStack();
                State.setState({
                  dialogSize: null,
                  dialogLayoutMode: null,
                  dialogIsFullScreen: false,
                });
                setConfirmExit(false);
              }}
            >
              Exit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
